import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useTranslation } from "react-i18next";

function formatDuration(summary) {
  if (summary?.durationLabel) return summary.durationLabel;
  if (summary?.durationSec != null && Number(summary.durationSec) > 0) {
    const sec = Math.round(Number(summary.durationSec));
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return s ? `${m}min ${s}s` : `${m} min`;
  }
  return null;
}

function DetailToggle({ open, onToggle, labelOpen, labelClosed }) {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  return (
    <Button
      size="small"
      fullWidth
      onClick={onToggle}
      endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      sx={{
        justifyContent: "space-between",
        textTransform: "none",
        fontWeight: 600,
        fontSize: "0.8125rem",
        minHeight: 34,
        px: 1.1,
        color: "text.primary",
        border: `1px solid ${alpha(accent, 0.28)}`,
        bgcolor: alpha(accent, 0.08),
        "&:hover": {
          borderColor: accent,
          bgcolor: alpha(accent, 0.14),
        },
      }}
    >
      {open ? labelOpen : labelClosed}
    </Button>
  );
}

/**
 * Artefatos pós-chamada no compromisso: resumo IA, chat, transcrição e gravação.
 */
export default function AgendaVideoCallSummary({ summary }) {
  const { t } = useTranslation("teamAgenda");
  const theme = useTheme();
  const [videoError, setVideoError] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [recordingOpen, setRecordingOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const transcriptLines = Array.isArray(summary?.transcriptLines) ? summary.transcriptLines : [];
  const transcriptText = String(summary?.transcriptText || "").trim();
  const hasTranscriptContent = transcriptLines.length > 0 || Boolean(transcriptText);
  const chatMessages = Array.isArray(summary?.chatMessages) ? summary.chatMessages : [];
  const meetingChatUrl = String(summary?.meetingChatUrl || "").trim() || null;
  const hasMeetingChat = chatMessages.length > 0 || Boolean(summary?.hasMeetingChat);
  const recordingUrl = summary?.recordingUrl || null;

  useEffect(() => {
    setVideoError(false);
  }, [recordingUrl]);

  if (!summary || typeof summary !== "object") return null;

  const durationLabel = formatDuration(summary);
  const participants = Array.isArray(summary.participants) ? summary.participants : [];
  const endReason = summary.endReasonLabel || null;
  const secondaryBits = [
    summary.qualityLabel || null,
    summary.mediaRegion
      ? `${t("videoSummary.region", "Região")}: ${summary.mediaRegion}`
      : null,
  ].filter(Boolean);

  const aiSummary = String(summary.aiSummary || "").trim();
  const keyPoints = Array.isArray(summary.keyPoints)
    ? summary.keyPoints.map((x) => String(x || "").trim()).filter(Boolean)
    : [];
  const actionItems = Array.isArray(summary.actionItems)
    ? summary.actionItems.map((x) => String(x || "").trim()).filter(Boolean)
    : [];
  const hasAiBlock = Boolean(aiSummary || keyPoints.length || actionItems.length);

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.08 : 0.03),
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
        <VideocamOutlinedIcon fontSize="small" color="action" />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
            {t("videoSummary.title", "Resumo da videoconferência")}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {[durationLabel, endReason, ...secondaryBits].filter(Boolean).join(" · ") ||
              t("videoSummary.hint", "Resumo, transcrição, chat e gravação desta reunião")}
          </Typography>
        </Box>
      </Stack>

      {participants.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          <Box component="span" fontWeight={700} color="text.primary">
            {t("videoSummary.participants", "Participantes")}:{" "}
          </Box>
          {participants
            .map((p) => `${p.name || p.identity || "?"}${p.durationLabel ? ` (${p.durationLabel})` : ""}`)
            .join(", ")}
        </Typography>
      ) : null}

      <Box sx={{ mb: 1.25 }}>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.45 }}>
          <AutoAwesomeOutlinedIcon sx={{ fontSize: 14, color: "primary.main" }} />
          <Typography variant="caption" fontWeight={700}>
            {t("videoSummary.aiTitle", "Resumo")}
          </Typography>
        </Stack>
        {hasAiBlock ? (
          <>
            {aiSummary ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.8125rem", lineHeight: 1.45, mb: keyPoints.length || actionItems.length ? 0.75 : 0 }}
              >
                {aiSummary}
              </Typography>
            ) : null}
            {keyPoints.length > 0 ? (
              <Box sx={{ mb: actionItems.length ? 0.75 : 0 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {t("videoSummary.keyPoints", "Pontos principais")}
                </Typography>
                <List dense disablePadding sx={{ mt: 0.15 }}>
                  {keyPoints.map((item, idx) => (
                    <ListItem key={`kp-${idx}`} disableGutters sx={{ py: 0.1, alignItems: "flex-start" }}>
                      <Typography component="span" sx={{ mr: 0.75, fontSize: "0.75rem", color: "text.secondary" }}>
                        •
                      </Typography>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{ variant: "caption", color: "text.secondary", sx: { lineHeight: 1.4 } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : null}
            {actionItems.length > 0 ? (
              <Box>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {t("videoSummary.actionItems", "Próximos passos")}
                </Typography>
                <List dense disablePadding sx={{ mt: 0.15 }}>
                  {actionItems.map((item, idx) => (
                    <ListItem key={`ai-${idx}`} disableGutters sx={{ py: 0.1, alignItems: "flex-start" }}>
                      <Typography component="span" sx={{ mr: 0.75, fontSize: "0.75rem", color: "text.secondary" }}>
                        •
                      </Typography>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{ variant: "caption", color: "text.secondary", sx: { lineHeight: 1.4 } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : null}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem", lineHeight: 1.45 }}>
            {hasTranscriptContent
              ? t(
                  "videoSummary.aiUnavailable",
                  "Resumo automático indisponível. Confira a transcrição abaixo."
                )
              : t(
                  "videoSummary.aiEmpty",
                  "Não houve falas suficientes para gerar um resumo automático."
                )}
          </Typography>
        )}
      </Box>

      <Stack spacing={0.75}>
        {hasMeetingChat ? (
          <Box>
            <DetailToggle
              open={chatOpen}
              onToggle={() => setChatOpen((v) => !v)}
              labelClosed={t("videoSummary.showChat", "Ver chat da reunião")}
              labelOpen={t("videoSummary.hideChat", "Ocultar chat")}
            />
            <Collapse in={chatOpen}>
              <Box
                sx={{
                  mt: 0.75,
                  p: 1.1,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: alpha(theme.palette.grey[500], 0.06),
                  maxHeight: 220,
                  overflow: "auto",
                }}
              >
                {chatMessages.length ? (
                  <Stack spacing={0.85}>
                    {chatMessages.map((msg, idx) => (
                      <Box key={msg.id != null ? `chat-${msg.id}` : `chat-${idx}`}>
                        <Typography variant="caption" fontWeight={700} color="text.primary">
                          {msg.senderName || t("videoSummary.chatParticipant", "Participante")}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.8125rem", whiteSpace: "pre-wrap", lineHeight: 1.4 }}
                        >
                          {msg.body}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t("videoSummary.chatEmpty", "Nenhuma mensagem no chat desta reunião.")}
                  </Typography>
                )}
                {meetingChatUrl && /^https?:\/\//i.test(meetingChatUrl) ? (
                  <Button
                    size="small"
                    href={meetingChatUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}
                  >
                    {t("videoSummary.openChatLink", "Abrir histórico do chat")}
                  </Button>
                ) : null}
              </Box>
            </Collapse>
          </Box>
        ) : null}

        {hasTranscriptContent ? (
          <Box>
            <DetailToggle
              open={transcriptOpen}
              onToggle={() => setTranscriptOpen((v) => !v)}
              labelClosed={t("videoSummary.showTranscript", "Ver transcrição")}
              labelOpen={t("videoSummary.hideTranscript", "Ocultar transcrição")}
            />
            <Collapse in={transcriptOpen}>
              <Box
                sx={{
                  mt: 0.75,
                  p: 1.1,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: alpha(theme.palette.grey[500], 0.06),
                  maxHeight: 260,
                  overflow: "auto",
                }}
              >
                {transcriptLines.length ? (
                  <Stack spacing={0.75}>
                    {transcriptLines.map((line, idx) => (
                      <Box key={`tr-${idx}`}>
                        <Typography variant="caption" fontWeight={700}>
                          {line.who || t("videoSummary.chatParticipant", "Participante")}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.8125rem", whiteSpace: "pre-wrap", lineHeight: 1.4 }}
                        >
                          {line.text}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.8125rem", whiteSpace: "pre-wrap", lineHeight: 1.45 }}
                  >
                    {transcriptText}
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Box>
        ) : null}

        {recordingUrl ? (
          <Box>
            <DetailToggle
              open={recordingOpen}
              onToggle={() => setRecordingOpen((v) => !v)}
              labelClosed={t("videoSummary.showRecording", "Ver gravação")}
              labelOpen={t("videoSummary.hideRecording", "Ocultar gravação")}
            />
            <Collapse in={recordingOpen}>
              <Box sx={{ mt: 0.75 }}>
                {videoError ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <VideocamOffIcon fontSize="small" color="disabled" />
                    <Button
                      size="small"
                      href={recordingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      {t("videoSummary.openRecording", "Abrir gravação")}
                    </Button>
                  </Stack>
                ) : (
                  <Box
                    component="video"
                    controls
                    src={recordingUrl}
                    onError={() => setVideoError(true)}
                    sx={{
                      width: "100%",
                      maxHeight: 280,
                      borderRadius: 1.5,
                      bgcolor: "common.black",
                    }}
                  />
                )}
              </Box>
            </Collapse>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {t("videoSummary.recordingPending", "A gravação aparece aqui quando a reunião for encerrada.")}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
